"use client";

import React, { useState, useEffect } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
} from "lucide-react";
import BookingTable from "../common/Table";
import DynamicForm from "./Popup";
import Button from "./Button";

import {
  useGetTermConditionsDetails,
  useAddTermConditionsDetails,
  useUpdateTermConditionsDetails,
  useTermConditionsPolicyStatus,
  useGetCountries,
  useGetAllBookingTypes,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";

interface TermCondition {
  id: number;
  country_id: string;
  booking_type: string;
  title: string;
  description: string;
  created_by: string;
  status: number; // 0 or 1
  country_name: string;
  package_name: string;
}

interface Country {
  id: number;
  code: string;
  name: string;
  nationality: string;
  phone_code: number;
}

interface BookingType {
  id: number;
  booking_type: string;
  menu_order: number | null;
  created_date: string;
  created_by: number;
  modified_by: number;
  modified_date: string;
  status: number;
  ip: string | null;
}

const ManageTermConditions: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTermCondition, setCurrentTermCondition] = useState<TermCondition | null>(null);
  const [termConditions, setTermConditions] = useState<TermCondition[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  // Hooks - useQuery hooks auto-fetch data on component mount
  const getTermConditions = useGetTermConditionsDetails();
  const addTermCondition = useAddTermConditionsDetails();
  const updateTermCondition = useUpdateTermConditionsDetails();
  const updateStatus = useTermConditionsPolicyStatus();
  const getCountries = useGetCountries(); // Auto-fetches countries
  const getBookingTypes = useGetAllBookingTypes(); // Auto-fetches booking types

  const user = useAuth();

  console.log("Logged in user:", user?.user?.id);
  
  // Fetch term conditions on component mount
  useEffect(() => {
    fetchTermConditions();
  }, []);

  const fetchTermConditions = () => {
    getTermConditions.mutate(
      {},
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            setTermConditions(data.data);
          }
        },
        onError: (error) => {
          console.error("Error fetching term conditions:", error);
        },
      }
    );
  };

  // Transform countries data for form options
  const countryOptions =
    getCountries.data?.map((country: Country) => ({
      value: country.id.toString(),
      label: country.name,
    })) || [];

  // Transform booking types data for form options
  const bookingTypeOptions =
    getBookingTypes.data?.data?.map((bookingType: BookingType) => ({
      value: bookingType.id.toString(),
      label: bookingType.booking_type,
    })) || [];

  const formFields: any = [
    {
      id: "country_id",
      label: "Country *",
      type: "select",
      required: true,
      defaultValue: currentTermCondition ? currentTermCondition.country_id : "",
      options: countryOptions,
      loading: getCountries.isLoading,
    },
    {
      id: "booking_type",
      label: "Booking Type *",
      type: "select",
      required: true,
      defaultValue: currentTermCondition ? currentTermCondition.booking_type : "",
      options: bookingTypeOptions,
      loading: getBookingTypes.isLoading,
    },
    {
      id: "title",
      label: "Title *",
      type: "text",
      required: true,
      placeholder: "Enter terms title",
      defaultValue: currentTermCondition ? currentTermCondition.title : "",
    },
    {
      id: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Enter terms description",
      defaultValue: currentTermCondition ? currentTermCondition.description : "",
    },
  ];

  const handleFormSubmit = (formData: any) => {
    const loggedInUser = user?.user?.id;
    const payload = {
      ...formData,
      ip: "127.0.0.1",
      created_by: loggedInUser,
      modified_by: loggedInUser,
    };

    if (currentTermCondition) {
      // Update existing term condition - keep existing status
      updateTermCondition.mutate(
        {
          ...payload,
          auto_id: currentTermCondition.id,
          status: currentTermCondition.status, // Keep existing status for updates
        },
        {
          onSuccess: (data) => {
            if (data.status === "success") {
              fetchTermConditions();
              setIsModalOpen(false);
              setCurrentTermCondition(null);
            }
          },
        }
      );
    } else {
      // Add new term condition - set status to 1 initially
      addTermCondition.mutate(
        {
          ...payload,
          status: 1, // Set status to 1 initially for new records
        },
        {
          onSuccess: (data) => {
            if (data.status === "success") {
              fetchTermConditions();
              setIsModalOpen(false);
            }
          },
        }
      );
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        status: 1,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchTermConditions();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: user?.user?.id.toString(),
        status: 0,
      },
      {
        onSuccess: (data) => {
          if (data.status === "success") {
            fetchTermConditions();
            setSelectedRows([]);
          }
        },
      }
    );
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;
    
    if (confirm("Are you sure you want to delete selected term conditions?")) {
      // You might need to implement a delete endpoint or use the status update with status = 2 for deletion
      console.log("Delete functionality to be implemented for IDs:", selectedRows);
      // For now, just clear selection
      setSelectedRows([]);
    }
  };

  // Transform API data to match table structure
  const tableData = termConditions.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    country: item.country_name,
    bookingType: item.package_name,
    title: item.title,
    description: item.description,
    createdBy: item.created_by,
    status: item.status, // 0 or 1
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter(id => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    {
      key: "sno",
      header: "S.No",
      sortable: true,
    },
    {
      key: "country",
      header: "Country",
      sortable: true,
    },
    {
      key: "bookingType",
      header: "Booking Type",
      sortable: true,
    },
    {
      key: "title",
      header: "Title",
      sortable: true,
    },
    {
      key: "description",
      header: "Description",
      sortable: true,
    },
    {
      key: "createdBy",
      header: "Created By",
      sortable: true,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (item: any) => {
        return (
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${
                item === 1 ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm">
              {item === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentTermCondition(row.originalData);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                if (confirm("Are you sure you want to delete this term condition?")) {
                  setSelectedRows([row.id]);
                  handleDelete();
                }
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map(row => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              setCurrentTermCondition(null);
              setIsModalOpen(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
            disabled={getCountries.isLoading || getBookingTypes.isLoading}
          >
            {getCountries.isLoading || getBookingTypes.isLoading
              ? "Loading..."
              : "Add Term Condition"}
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Publishing..." : "Publish"}
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={getTermConditions.isPending}
          // Add select all functionality
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === tableData.length && tableData.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentTermCondition(null);
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentTermCondition
                    ? "Update Term Condition"
                    : "Add Term Condition"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentTermCondition(null);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields}
                  onSubmit={handleFormSubmit}
                  submitText={
                    currentTermCondition ? "Update Term Condition" : "Add Term Condition"
                  }
                  loading={
                    addTermCondition.isPending ||
                    updateTermCondition.isPending
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTermConditions;



